import type { Route } from './lib/router.svelte'

/**
 * The route table. Every chunk is loaded lazily, so the first paint carries
 * the shell and the home page and nothing else — a visitor who never opens
 * the account pages never downloads them.
 *
 * `description` is not decoration: the router writes it into the meta tags on
 * navigation, and `scripts/prerender.mjs` reads this same table to patch the
 * static HTML per route so crawlers and link unfurlers see real content.
 */

export const ROUTES: Route[] = [
  {
    path: '/',
    component: () => import('./routes/Home.svelte'),
    title: 'Mountit',
    description:
      'Mount SFTP, WebDAV, S3 and 38 cloud storage services in Finder on macOS, and in the Files app on iPadOS and visionOS. A Rust core, no kernel extensions.',
  },
  {
    path: '/features',
    component: () => import('./routes/Features.svelte'),
    title: 'Features',
    description:
      'A caching VFS built for Finder, a mount with no kernel extension, credentials in the Keychain, and honest limits where a protocol has them.',
  },
  {
    path: '/protocols',
    component: () => import('./routes/Protocols.svelte'),
    title: 'Protocols',
    description:
      'Every storage service Mountit can mount, with the evidence level for each — live-tested, emulator-tested, or not yet tested against a real server.',
  },
  {
    path: '/backends',
    component: () => import('./routes/Backends.svelte'),
    title: 'Backends',
    description:
      'All 38 storage services Mountit mounts — SFTP, S3 and its presets, WebDAV clouds, FTP and more — each with its evidence level from the engineering ledger.',
  },
  {
    path: '/backends/:slug',
    component: () => import('./routes/BackendArticle.svelte'),
    title: 'Backends',
    description: 'What Mountit does with this storage service, and where its evidence stands.',
  },
  {
    // The Live Compatibility Ledger. Generated in full from the app's own
    // conformance ledger (`crates/mountit-conformance/status.json`) — see
    // src/lib/content/compatibility.ts, which has no data typed into it.
    // This is the credibility asset: it publishes the findings, including the
    // ones that say a service we ship is not conformance-clean.
    path: '/compatibility',
    component: () => import('./routes/Compatibility.svelte'),
    title: 'Compatibility ledger',
    description:
      'Where every storage service actually stands: run against a real server or not, and every conformance finding that blocks one — from the app’s own CI gate.',
  },
  {
    path: '/pricing',
    component: () => import('./routes/Pricing.svelte'),
    title: 'Pricing',
    description:
      'Two editions — Standard on the Mac App Store at EUR 29 once or EUR 12 a year, and Mountit Pro sold directly — plus a free tier and a fourteen-day trial.',
  },
  {
    path: '/download',
    component: () => import('./routes/Download.svelte'),
    title: 'Download',
    description:
      'Download Mountit Pro for macOS, or get the Standard edition from the Mac App Store for macOS, iPadOS and visionOS.',
  },
  {
    path: '/docs',
    component: () => import('./routes/Docs.svelte'),
    title: 'Documentation',
    description: 'Install Mountit, enable the file system extension, activate a licence and mount your first volume.',
  },
  {
    path: '/docs/:slug',
    component: () => import('./routes/DocArticle.svelte'),
    title: 'Documentation',
    description: 'Mountit documentation.',
  },
  {
    // The highest-intent query in this category is not "mount S3 on Mac", it
    // is "macFUSE alternative" — and the honest answer names options that are
    // not ours. See src/lib/content/macfuse.ts for the sourcing rule.
    path: '/macfuse',
    component: () => import('./routes/Macfuse.svelte'),
    title: 'macFUSE on modern macOS',
    description:
      'What actually changed for macFUSE on modern macOS — kernel extensions, Apple silicon boot policy, the licence — and the alternatives, including rclone and SMB.',
  },
  {
    // The comparison /macfuse does not make: commercial products, not mount
    // mechanisms. See src/lib/content/compare.ts for the sourcing rule —
    // identical to macfuse.ts's, because the risk is identical.
    path: '/compare',
    component: () => import('./routes/Compare.svelte'),
    title: 'Mountit vs ExpanDrive, Mountain Duck and more',
    description:
      'ExpanDrive, Mountain Duck, CloudMounter, Transmit, ForkLift and Cyberduck compared with Mountit on price and how each one mounts a drive — sourced, not guessed.',
  },
  {
    // The no-lock-in page. It publishes the real rclone stanza that reads
    // Mountit's own crypt-wrapped data, because the claim is only worth
    // anything if the configuration is on the page.
    path: '/exit',
    component: () => import('./routes/Exit.svelte'),
    title: 'Leaving Mountit',
    description:
      'Your data stays readable without us: the rclone crypt configuration that opens Mountit-encrypted storage, setting by setting, and what it cannot represent.',
  },
  {
    // Was a meta-refresh alias to /news until it had something to say.
    path: '/security',
    component: () => import('./routes/Security.svelte'),
    title: 'Security',
    description:
      'Mountit’s threat model, every host the app can contact, a falsifiable no-telemetry claim, the gaps in our own audit, and the SBOM each release will carry.',
  },
  {
    path: '/support',
    component: () => import('./routes/Support.svelte'),
    title: 'Support',
    description: 'Get help with Mountit — activation, mounting, billing and refunds. A real person answers.',
  },
  {
    path: '/changelog',
    component: () => import('./routes/Changelog.svelte'),
    title: 'Changelog',
    description:
      'Every Mountit release, newest first, with security entries tagged and sorted to the top of their release — never buried in “bug fixes and improvements”.',
  },
  {
    path: '/news',
    component: () => import('./routes/News.svelte'),
    title: 'News',
    description:
      'Mountit announcements and security notices. No posting schedule and no filler — when the list is short, there was nothing to say.',
  },
  {
    // Per-post pages, one stable URL each: /news/<slug> is what the news
    // feed, the sitemap and the app's announcement links all point at.
    path: '/news/:slug',
    component: () => import('./routes/NewsArticle.svelte'),
    title: 'News',
    description: 'Mountit announcements and security notices.',
  },
  {
    // The permanent destination of the app's `offer` announcements — see
    // src/lib/content/offers.ts. A campaign is an entry there, not a page.
    path: '/offers',
    component: () => import('./routes/Offers.svelte'),
    title: 'Offers',
    description:
      'Every Mountit offer campaign with its code, dates and full terms — the permanent destination of the offer banners inside the app.',
  },

  {
    path: '/account',
    component: () => import('./routes/ComingSoon.svelte'),
    title: 'Comptes clients',
    description: 'Les comptes clients et la boutique Mountit ouvriront prochainement.',
    // No accounts exist yet; a crawler indexing a "coming soon" shell buys
    // nothing. vite.config.ts's robots comment promises this flag is set.
    noIndex: true,
  },

  // --- Mentions légales (français, obligatoires) --------------------------
  // The privacy policy in particular is a hard prerequisite for OAuth
  // verification with Google, Microsoft, Dropbox and Box: each requires a
  // publicly reachable policy on the same domain as the OAuth client.
  {
    path: '/legal/mentions-legales',
    component: () => import('./routes/legal/MentionsLegales.svelte'),
    title: 'Mentions légales',
    description: 'Éditeur, hébergeur et directeur de la publication du site mountit.app.',
  },
  {
    path: '/legal/cgu',
    component: () => import('./routes/legal/Cgu.svelte'),
    title: 'Conditions générales d’utilisation',
    description: 'Les règles d’utilisation du site mountit.app et du logiciel Mountit.',
  },
  {
    path: '/legal/confidentialite',
    component: () => import('./routes/legal/Confidentialite.svelte'),
    title: 'Politique de confidentialité',
    description:
      'Ce que Mountit collecte, pourquoi, et la liste bien plus longue de ce que nous ne voyons jamais. Inclut la Limited Use disclosure des API Google.',
  },
  {
    path: '/legal/cgv',
    component: () => import('./routes/legal/Cgv.svelte'),
    title: 'Conditions générales de vente',
    description: 'Les conditions qui régiront la vente de licences Mountit à l’ouverture de la boutique.',
  },
  {
    path: '/legal/cookies',
    component: () => import('./routes/legal/Cookies.svelte'),
    title: 'Cookies et traceurs',
    description: 'Ce site ne dépose aucun cookie. Voici pourquoi.',
  },
  {
    // The English set. The app's own links and every OAuth reviewer land on
    // these; `/terms` and `/privacy` redirect here (vite.config.ts REDIRECTS).
    path: '/legal/privacy',
    component: () => import('./routes/legal/Privacy.svelte'),
    title: 'Privacy policy',
    description:
      'What Mountit collects, why, and for how long. Includes the Google API Services Limited Use disclosure.',
  },
  {
    path: '/legal/terms',
    component: () => import('./routes/legal/Terms.svelte'),
    title: 'Terms of use',
    description: 'The rules for using the mountit.app website and the Mountit software.',
  },
  {
    path: '/legal/license',
    component: () => import('./routes/legal/License.svelte'),
    title: 'End-user licence agreement',
    description: 'The end-user licence agreement for the Mountit software, in English.',
  },
  {
    path: '/legal/sous-traitance',
    component: () => import('./routes/legal/SousTraitance.svelte'),
    title: 'Sous-traitance et RGPD',
    description:
      'Pourquoi Mountit n’est pas votre sous-traitant au sens de l’article 28 du RGPD, et ce que nous signons à la place.',
  },
  {
    path: '/legal/licence',
    component: () => import('./routes/legal/Licence.svelte'),
    title: 'Contrat de licence',
    description: 'Le contrat de licence utilisateur final du logiciel Mountit.',
  },
]

export const NOT_FOUND: Route = {
  path: '*',
  component: () => import('./routes/NotFound.svelte'),
  title: 'Page not found',
  description: 'That page does not exist.',
  noIndex: true,
}
